import type { TutorProfileAttributes, UserAttributes } from '@/server/models'
import type { OneOnOneProfile } from '@/server/models/TutorProfile'

import { SQL, type SQLFragment } from '@/client/query'
import { AUDITOR_LEVELS } from '@/client/services/sessionAudit/constants'
import { TutorAuditorLevel } from '@/client/services/sessionAudit/types'

import {
  type GroupBy,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryOrderToSQL,
} from './common'
import TutorAuditQ from './TutorAuditQ'
import UserQ from './UserQ'

const TutorProfileQ = {
  ...makeQueryModelV2<TutorProfileAttributes>('tutor_profile')([
    'id',
    'user_id',
    'details',
    'mentor_id',
    'mentor_remarks',
    'approved_at',
    'review_reason',
    'training_reflections',
    'internal_state',
    'created_at',
    'updated_at',
    'deleted_at',
    'auditor_level',
    'scheduled_help_details',
  ]),

  where: (
    {
      paranoid = true,
      approved,
      id,
      userId,
      mentorId,
      includeUser,
      groupBy = null,
      order = null,
    }: {
      paranoid?: boolean
      approved?: boolean
      id?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      mentorId?: number | null | SQLFragment<number>
      includeUser?: boolean
      groupBy?: GroupBy | null
      order?: QueryOrder | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<TutorProfileAttributes> => SQL`
        FROM ${TutorProfileQ}
        ${
          includeUser === true
            ? SQL`INNER JOIN ${UserQ} ON ${UserQ.id} = ${TutorProfileQ.user_id}`
            : SQL``
        }
        WHERE
            ${id != null ? SQL`${TutorProfileQ.id} = ${id} AND` : SQL``}
            ${userId != null ? SQL`${TutorProfileQ.user_id} = ${userId} AND` : SQL``}
            ${mentorId != null ? SQL`${TutorProfileQ.mentor_id} = ${mentorId} AND` : SQL``}
            ${
              approved != null
                ? SQL`${TutorProfileQ.approved_at} IS ${approved ? SQL`NOT` : SQL``} NULL AND`
                : SQL``
            }
            ${paranoid ? SQL`${TutorProfileQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
    `,

  hasAuditorLevel: (level: TutorAuditorLevel): SQLFragment<boolean> =>
    SQL`${TutorProfileQ.auditor_level} = ${level}`,

  hasAnyAuditorLevel: (levels: TutorAuditorLevel[]): SQLFragment<boolean> =>
    SQL`${TutorProfileQ.auditor_level} = ANY(${levels})`,

  hasAtLeastAuditorLevel: (level: TutorAuditorLevel): SQLFragment<boolean> =>
    TutorProfileQ.hasAnyAuditorLevel(AUDITOR_LEVELS.slice(AUDITOR_LEVELS.indexOf(level))),

  getUser: (): SQLFragment<UserAttributes> => SQL`
        ${UserQ.where({
          id: TutorProfileQ.user_id,
        })}
    `,
  getMentor: (): SQLFragment<UserAttributes> => SQL`
        ${UserQ.where({
          id: TutorProfileQ.mentor_id,
        })}
    `,
  getOneOnOneBio: (): SQLFragment<string> =>
    SQL`${TutorProfileQ.internal_state}->'one_on_one_profile'->>'bio'`,

  hasSlackEmail: (): SQLFragment<boolean> =>
    SQL`${TutorProfileQ.internal_state}->>'slack_email' IS NOT NULL`,
  getSlackEmail: (): SQLFragment<string> => SQL`${TutorProfileQ.internal_state}->>'slack_email'`,
  getSlackUserId: (): SQLFragment<string> => SQL`${TutorProfileQ.internal_state}->>'slack_user_id'`,

  getOneOnOneIsActive: (): SQLFragment<boolean> =>
    SQL`(${TutorProfileQ.internal_state}->'one_on_one_profile'->>'is_active')::boolean`,
  getOneOnOneProfile: (): SQLFragment<OneOnOneProfile> =>
    SQL`${TutorProfileQ.internal_state}->'one_on_one_profile'`,

  getAuditReviewThreshold: (): SQLFragment<number> => SQL`(CASE
    WHEN ${TutorProfileQ.hasAuditorLevel(TutorAuditorLevel.Advanced)} THEN 30
    WHEN ${TutorProfileQ.hasAuditorLevel(TutorAuditorLevel.Intermediate)} THEN 15
    WHEN ${TutorProfileQ.hasAuditorLevel(TutorAuditorLevel.Beginner)} THEN 3
    WHEN ${TutorProfileQ.hasAuditorLevel(TutorAuditorLevel.Novice)} THEN 1
    ELSE 0 END)`,

  getFirstAuditThatNeedsReviewDate: (): SQLFragment<string> => SQL`
    (SELECT ${TutorAuditQ.created_at}
      ${TutorAuditQ.getAuditsSinceLastLevelChange(TutorProfileQ.user_id)}
        ORDER BY ${TutorAuditQ.created_at} ASC
        OFFSET (SELECT ${TutorProfileQ.getAuditReviewThreshold()})
        LIMIT 1
    )
  `,

  isOverdueForAuditorLevelReview: (): SQLFragment<boolean> => SQL`
    (SELECT ${TutorProfileQ.getFirstAuditThatNeedsReviewDate()} IS NOT NULL)
  `,
}

export default TutorProfileQ
